import { useCallback, useState } from "react";
import { localPoint } from "@visx/event";
import { useTooltip, useTooltipInPortal } from "@visx/tooltip";
import { ScaleTime } from "d3";

import { WorkerUsageDay } from "../types";
import { CONCURRENCY_CHART_MARGIN } from "./constants";

// Tracks the recorded day closest to the pointer and feeds it to the tooltip.
// The portal container is the <svg> itself, so tooltip coordinates stay correct
// however far the wrapper is scrolled.
const useConcurrencyChartHover = (
  recordedDays: WorkerUsageDay[],
  xScale: ScaleTime<number, number>
) => {
  const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } =
    useTooltip<WorkerUsageDay>();
  const { containerRef, TooltipInPortal } = useTooltipInPortal({ scroll: true });

  const [hoveredDay, setHoveredDay] = useState<number | undefined>(undefined);

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<SVGRectElement>) => {
      const point = localPoint(event);
      if (!point) return;

      const pointerDate = xScale.invert(point.x - CONCURRENCY_CHART_MARGIN.left);

      if (recordedDays.length === 0) return;

      const closestDay = recordedDays.reduce((acc, next) =>
        Math.abs(next.day.getTime() - pointerDate.getTime()) <
        Math.abs(acc.day.getTime() - pointerDate.getTime())
          ? next
          : acc
      );

      setHoveredDay(closestDay.day.getTime());

      showTooltip({
        tooltipLeft: xScale(closestDay.day) + CONCURRENCY_CHART_MARGIN.left,
        tooltipTop: point.y,
        tooltipData: closestDay,
      });
    },
    [recordedDays, showTooltip, xScale]
  );

  const handleMouseLeave = useCallback(() => {
    setHoveredDay(undefined);
    hideTooltip();
  }, [hideTooltip]);

  // The tooltip is anchored in SVG coords, so scrolling would drag it off-window.
  const handleScroll = useCallback(() => {
    hideTooltip();
    setHoveredDay(undefined);
  }, [hideTooltip]);

  return {
    hoveredDay,
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    containerRef,
    TooltipInPortal,
    handleMouseMove,
    handleMouseLeave,
    handleScroll,
  };
};

export default useConcurrencyChartHover;
